/**
 * Authentication Utilities
 *
 * Provides functions to determine user authentication state and role
 * for proper separation between Admin and User apps.
 */

import {
  getAccessToken,
  getUserInfoFromLocalStorage,
} from "@/lib/storeManager";

export type UserRole = "admin" | "user" | null;

export interface AuthState {
  isAuthenticated: boolean;
  role: UserRole;
  isAdmin: boolean;
  isUser: boolean;
}

/**
 * Determines if the current user is an Admin
 * Admin is identified by:
 * - Having an accessToken (from email/password login)
 * - NOT having userMobile (mobile OTP users are not admins)
 * - Having userInfo with user_type (stored in localStorage)
 */
export const isAdmin = (): boolean => {
  const accessToken = getAccessToken();
  const userMobile = localStorage.getItem("userMobile");

  // Admin must have accessToken (from email/password login)
  if (!accessToken) {
    return false;
  }

  // If userMobile exists, it's a mobile OTP login (user), not admin
  if (userMobile) {
    return false;
  }

  // If accessToken exists and no userMobile, consider it admin
  // You can add more specific checks based on user_type if needed
  // const userInfo = getUserInfoFromLocalStorage();
  // if (userInfo?.user_type) { /* check user_type */ }
  return true;
};

/**
 * Determines if the current user is a regular User (mobile OTP login)
 * User is identified by:
 * - Having userMobile in localStorage (primary identifier for mobile OTP login)
 * - Having userInfo in localStorage
 * - May have accessToken (mobile OTP login also sets accessToken)
 */
export const isUser = (): boolean => {
  const userMobile = localStorage.getItem("userMobile");
  const userInfo = getUserInfoFromLocalStorage();

  // User must have mobile number and userInfo
  // Note: Mobile OTP login also sets accessToken, so we check userMobile first
  return !!(userMobile && userInfo);
};

/**
 * Gets the current user role
 */
export const getUserRole = (): UserRole => {
  if (isAdmin()) {
    return "admin";
  }
  if (isUser()) {
    return "user";
  }
  return null;
};

/**
 * Checks if user is authenticated (either Admin or User)
 */
export const isAuthenticated = (): boolean => {
  return isAdmin() || isUser();
};

/**
 * Gets complete authentication state
 */
export const getAuthState = (): AuthState => {
  const role = getUserRole();
  return {
    isAuthenticated: role !== null,
    role,
    isAdmin: role === "admin",
    isUser: role === "user",
  };
};

/**
 * Clears all authentication data
 */
export const clearAuth = (): void => {
  localStorage.clear();
};
